import { Button, Icon, type ButtonProps } from '@chakra-ui/react';
import { isDefined } from '@duke-hq/lib';

export type ActionButtonProps = {
  modalOpenButtonVariant?: 'primary' | 'secondary' | 'outline';
  modalOpenButtonText: string | React.ReactNode;
  modalOpenButtonLeftIcon?: React.ElementType;
  onOpen: () => void;
  modalOpenButtonTestId?: string;
  isDisabled?: boolean;
} & ButtonProps;

function PrimaryActionButton({
  modalOpenButtonText,
  modalOpenButtonLeftIcon,
  onOpen,
  modalOpenButtonTestId,
  isDisabled,
  ...buttonProps
}: ActionButtonProps): React.ReactElement {
  return (
    <Button
      size="sm"
      variant="solid"
      onClick={onOpen}
      isDisabled={isDisabled}
      leftIcon={
        isDefined(modalOpenButtonLeftIcon) ? <Icon as={modalOpenButtonLeftIcon} /> : undefined
      }
      data-testid={modalOpenButtonTestId}
      {...buttonProps}
    >
      {modalOpenButtonText}
    </Button>
  );
}

function SecondaryActionButton({
  modalOpenButtonText,
  modalOpenButtonLeftIcon,
  onOpen,
  modalOpenButtonTestId,
  isDisabled,
  ...buttonProps
}: ActionButtonProps): React.ReactElement {
  return (
    <Button
      size="sm"
      variant="solid"
      bg="bg.muted"
      color="fg.emphasis"
      colorScheme="gray"
      onClick={onOpen}
      isDisabled={isDisabled}
      leftIcon={
        isDefined(modalOpenButtonLeftIcon) ? <Icon as={modalOpenButtonLeftIcon} /> : undefined
      }
      data-testid={modalOpenButtonTestId}
      {...buttonProps}
    >
      {modalOpenButtonText}
    </Button>
  );
}

function OutlineActionButton({
  modalOpenButtonText,
  modalOpenButtonLeftIcon,
  onOpen,
  modalOpenButtonTestId,
  isDisabled,
  ...buttonProps
}: ActionButtonProps) {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onOpen}
      isDisabled={isDisabled}
      leftIcon={
        isDefined(modalOpenButtonLeftIcon) ? <Icon as={modalOpenButtonLeftIcon} /> : undefined
      }
      data-testid={modalOpenButtonTestId}
      {...buttonProps}
    >
      {modalOpenButtonText}
    </Button>
  );
}

export function ActionButton({
  modalOpenButtonText,
  modalOpenButtonLeftIcon,
  modalOpenButtonVariant = 'primary',
  onOpen,
  modalOpenButtonTestId,
  isDisabled,
  ...buttonProps
}: ActionButtonProps) {
  const commonProps = {
    modalOpenButtonText,
    modalOpenButtonLeftIcon,
    onOpen,
    modalOpenButtonTestId,
    isDisabled,
    ...buttonProps,
  };

  switch (modalOpenButtonVariant) {
    case 'secondary':
      return <SecondaryActionButton {...commonProps} />;
    case 'outline':
      return <OutlineActionButton {...commonProps} />;
    case 'primary':
    default:
      return <PrimaryActionButton {...commonProps} />;
  }
}
